// src/private/src/context/dashboard/DailyPulse.jsx
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchDailyPulseByClient,
  fetchBudgetPacing,
  fetchBudgetPacingOverall,
} from "@src/api";
import { useAuth } from "@src/context";
import { DEFAULT_CLIENT_STATUS_FILTER } from "@src/constants/clients";
import { formatDateToYMD, getCurrentMonthDates, getNewYorkNow } from "@src/utils/dateUtils";

const toFiniteNumber = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const numberOrZero = (value) => toFiniteNumber(value) ?? 0;

const roundToTwo = (value) => {
  const numeric = toFiniteNumber(value);
  if (numeric === null) {
    return 0;
  }
  return Math.round(numeric * 100) / 100;
};

const clampToZero = (value) => {
  const numeric = toFiniteNumber(value);
  if (numeric === null) {
    return 0;
  }
  return numeric < 0 ? 0 : numeric;
};

const deriveDaysRemaining = (budgetRemaining, remainingPerDay) => {
  const remaining = toFiniteNumber(budgetRemaining);
  const perDay = toFiniteNumber(remainingPerDay);
  if (remaining === null || perDay === null || perDay <= 0) {
    return null;
  }
  const value = remaining / perDay;
  return Number.isFinite(value) ? value : null;
};

const extractPacingMeta = (metrics) => {
  if (!metrics || typeof metrics !== 'object') {
    return {};
  }

  const meta = metrics.__pacing_metadata || metrics.pacing_metadata;
  if (meta && typeof meta === 'object') {
    return meta;
  }

  return {};
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const buildTrailingRange = (days, referenceDate = new Date()) => {
  const anchor = referenceDate instanceof Date ? referenceDate : new Date(referenceDate);
  if (!(anchor instanceof Date) || Number.isNaN(anchor.getTime())) {
    return null;
  }

  const end = new Date(anchor);
  end.setHours(0, 0, 0, 0);
  end.setDate(end.getDate() - 1);
  const start = new Date(end);
  start.setDate(end.getDate() - (days - 1));

  return {
    date_after: formatDateToYMD(start),
    date_before: formatDateToYMD(end),
  };
};

const getClientLookupKey = (row = {}) => {
  if (row.client_id) {
    return `client-${row.client_id}`;
  }
  return row.uuid || null;
};

const extractRowsFromResponse = (response) => {
  const payload = response?.data;
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.results)) {
    return payload.results;
  }
  return [];
};

const extractCountFromResponse = (response, fallbackLength = 0) => {
  const payload = response?.data;
  if (typeof payload?.count === 'number' && Number.isFinite(payload.count)) {
    return payload.count;
  }
  return fallbackLength;
};

const extractSummaryFromResponse = (response) => {
  const payload = response?.data;
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    return payload.summary && typeof payload.summary === 'object' ? payload.summary : null;
  }
  return null;
};

const buildCostOfMarketingLookup = (rows, buildKey) => {
  const map = new Map();
  if (!Array.isArray(rows)) {
    return map;
  }
  rows.forEach((row = {}) => {
    const key = buildKey(row);
    if (!key) {
      return;
    }
    const metrics = withMarketingMetrics(row);
    map.set(key, metrics.costOfMarketing ?? null);
  });
  return map;
};

const buildRollingLookups = (last30Rows, last60Rows, keyFn) => ({
  last30: buildCostOfMarketingLookup(last30Rows, keyFn),
  last60: buildCostOfMarketingLookup(last60Rows, keyFn),
});

const summarizeOverallBudgetRows = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return null;
  }

  const totals = rows.reduce(
    (acc, row = {}) => {
      acc.budget_amount += numberOrZero(row.budget_amount);
      acc.projected_spend += numberOrZero(row.projected_spend);
      acc.budget_remaining += numberOrZero(row.budget_remaining);
      acc.remaining_per_day += numberOrZero(row.remaining_per_day);
      acc.yesterday_spend += numberOrZero(row.yesterday_spend);
      return acc;
    },
    {
      budget_amount: 0,
      projected_spend: 0,
      budget_remaining: 0,
      remaining_per_day: 0,
      yesterday_spend: 0,
    }
  );

  return {
    ...totals,
    pacing_percentage:
      totals.budget_amount > 0 ? roundToTwo((totals.projected_spend / totals.budget_amount) * 100) : 0,
    calendar_budget_remaining_per_day: totals.remaining_per_day,
  };
};

const mergeClientSummary = (summary, overallSummary, rollingSummaries = {}) => {
  if (!summary && !overallSummary) {
    return null;
  }

  const merged = {
    uuid: 'daily-pulse-total-row',
    client__name: 'Total',
    ...(summary || {}),
    ...(overallSummary || {}),
  };

  if (overallSummary?.remaining_per_day !== undefined) {
    merged.remaining_per_day = overallSummary.remaining_per_day;
    merged.calendar_budget_remaining_per_day = overallSummary.remaining_per_day;
  }

  if (rollingSummaries.last30?.costOfMarketing !== undefined) {
    merged.costOfMarketingLast30 = rollingSummaries.last30.costOfMarketing;
  }
  if (rollingSummaries.last60?.costOfMarketing !== undefined) {
    merged.costOfMarketingLast60 = rollingSummaries.last60.costOfMarketing;
  }

  return merged;
};

const attachRollingCosts = (row, buildKey, rollingLookups = {}, includeRolling = true) => {
  if (!row) {
    return row;
  }
  if (!includeRolling) {
    return row;
  }
  const key = buildKey(row);
  const last30Lookup = rollingLookups.last30 ?? new Map();
  const last60Lookup = rollingLookups.last60 ?? new Map();

  return {
    ...row,
    costOfMarketingLast30: key ? last30Lookup.get(key) ?? null : null,
    costOfMarketingLast60: key ? last60Lookup.get(key) ?? null : null,
  };
};

const sumFiniteValues = (existing, addition) => {
  const current = typeof existing === 'number' && Number.isFinite(existing) ? existing : null;
  const extra = typeof addition === 'number' && Number.isFinite(addition) ? addition : null;

  if (current === null && extra === null) {
    return null;
  }

  return (current ?? 0) + (extra ?? 0);
};

const extractYesterdayFallbackMeta = (budget) => {
  if (!budget || typeof budget !== 'object') {
    return null;
  }

  const yesterdaySpendWithFees = toFiniteNumber(
    budget.yesterday_spend_with_fees ??
      budget.yesterday_spend ??
      budget?.__pacing_metadata?.yesterday_spend_with_fees ??
      budget?.pacing_metadata?.yesterday_spend_with_fees ??
      budget?.__pacing_metadata?.yesterday_spend ??
      budget?.pacing_metadata?.yesterday_spend
  );

  const monthThroughYesterdayWithFees = toFiniteNumber(
    budget.month_through_yesterday_with_fees ??
      budget?.__pacing_metadata?.month_through_yesterday_with_fees ??
      budget?.pacing_metadata?.month_through_yesterday_with_fees ??
      budget?.__pacing_metadata?.actual_through_yesterday_with_fees ??
      budget?.pacing_metadata?.actual_through_yesterday_with_fees
  );

  const actualThroughYesterdayWithFees = toFiniteNumber(
    budget?.__pacing_metadata?.actual_through_yesterday_with_fees ??
      budget?.pacing_metadata?.actual_through_yesterday_with_fees ??
      budget.month_through_yesterday_with_fees
  );

  if (
    yesterdaySpendWithFees === null &&
    monthThroughYesterdayWithFees === null &&
    actualThroughYesterdayWithFees === null
  ) {
    return null;
  }

  return {
    yesterday_spend_with_fees: yesterdaySpendWithFees,
    month_through_yesterday_with_fees: monthThroughYesterdayWithFees,
    actual_through_yesterday_with_fees: actualThroughYesterdayWithFees,
  };
};

const accumulateFallbackMeta = (map, key, meta) => {
  if (!key || !meta) {
    return;
  }
  const existing = map.get(key) ?? {};
  const combined = {
    yesterday_spend_with_fees: sumFiniteValues(
      existing.yesterday_spend_with_fees,
      meta.yesterday_spend_with_fees
    ),
    month_through_yesterday_with_fees: sumFiniteValues(
      existing.month_through_yesterday_with_fees,
      meta.month_through_yesterday_with_fees
    ),
    actual_through_yesterday_with_fees: sumFiniteValues(
      existing.actual_through_yesterday_with_fees,
      meta.actual_through_yesterday_with_fees
    ),
  };

  if (
    combined.yesterday_spend_with_fees === null &&
    combined.month_through_yesterday_with_fees === null &&
    combined.actual_through_yesterday_with_fees === null
  ) {
    return;
  }

  map.set(key, combined);
};

const buildYesterdayFallbackLookup = (budgets = []) => {
  const clients = new Map();
  const channels = new Map();

  if (!Array.isArray(budgets) || budgets.length === 0) {
    return { clients, channels };
  }

  budgets.forEach((budget) => {
    const meta = extractYesterdayFallbackMeta(budget);
    if (!meta) {
      return;
    }

    const clientId = budget.client;
    if (clientId !== null && clientId !== undefined) {
      const clientKey = `client-${clientId}`;
      accumulateFallbackMeta(clients, clientKey, meta);
      if (budget.channel) {
        const channelKey = `client-${clientId}-channel-${budget.channel}`;
        accumulateFallbackMeta(channels, channelKey, meta);
      }
    }
  });

  return { clients, channels };
};

const getDaysLeftInMonthIncludingToday = (inputDate) => {
  const reference =
    inputDate instanceof Date
      ? inputDate
      : inputDate
        ? new Date(inputDate)
        : new Date();
  if (!(reference instanceof Date) || Number.isNaN(reference.getTime())) {
    return null;
  }

  const year = reference.getFullYear();
  const month = reference.getMonth();
  const startOfTodayUTC = Date.UTC(year, month, reference.getDate());
  const startOfNextMonthUTC = Date.UTC(year, month + 1, 1);
  const diff = Math.floor((startOfNextMonthUTC - startOfTodayUTC) / MS_PER_DAY);

  if (!Number.isFinite(diff)) {
    return null;
  }

  return diff > 0 ? diff : 0;
};

const recalculateBudgetMetrics = (row, metrics, merged, options = {}) => {
  if (!row || !metrics) {
    return merged;
  }

  const { shouldFallbackToActuals = false, calendarToday } = options;

  const budgetAmount = toFiniteNumber(
    metrics.budget_amount ?? metrics.budget ?? merged.budget_amount ?? merged.budget
  );

  if (budgetAmount === null) {
    return merged;
  }

  const feeValue = toFiniteNumber(row.client_fee ?? merged.client_fee);
  const fee = feeValue ?? 0;
  const feeMultiplier = 1 + fee;

  const baseMeta = extractPacingMeta(metrics);
  const metaMonthThroughYesterdayWithFees = toFiniteNumber(
    metrics.month_through_yesterday_with_fees ??
      baseMeta.month_through_yesterday_with_fees ??
      baseMeta.actual_through_yesterday_with_fees
  );
  const metaActualMonthToDateWithFees = toFiniteNumber(
    metrics.actual_month_to_date_with_fees ??
      baseMeta.actual_month_to_date_with_fees ??
      baseMeta.spent_with_fees
  );
  const metaPacingMonthToDateWithFees = toFiniteNumber(
    metrics.pacing_month_to_date_with_fees ??
      metrics.month_to_date_with_fees ??
      baseMeta.pacing_month_to_date_with_fees ??
      baseMeta.month_to_date_with_fees ??
      baseMeta.month_through_yesterday_with_fees
  );
  const metaTodaySpendWithFees = toFiniteNumber(
    metrics.today_spend_with_fees ?? baseMeta.today_spend_with_fees
  );

  const parsedYesterdayWithFees = toFiniteNumber(
    baseMeta.yesterday_spend_with_fees ??
      metrics.yesterday_spend_with_fees ??
      merged.yesterday_spend_with_fees
  );
  const parsedYesterday = toFiniteNumber(
    baseMeta.yesterday_spend ?? metrics.yesterday_spend ?? merged.yesterday_spend
  );

  const hasWithFees = typeof parsedYesterdayWithFees === 'number';
  const hasRawYesterday = typeof parsedYesterday === 'number';

  const yesterdaySpendRaw = hasRawYesterday ? clampToZero(parsedYesterday) : 0;
  const yesterdaySpendWithFees = hasWithFees
    ? clampToZero(parsedYesterdayWithFees)
    : yesterdaySpendRaw * feeMultiplier;
  const hasValidYesterday = hasWithFees || hasRawYesterday;

  let daysRemaining = baseMeta.days_remaining;
  if (!(typeof daysRemaining === 'number' && Number.isFinite(daysRemaining))) {
    daysRemaining = deriveDaysRemaining(
      metrics.budget_remaining ?? merged.budget_remaining,
      metrics.remaining_per_day ?? merged.remaining_per_day
    );
  }

  const hasValidDaysRemaining =
    typeof daysRemaining === 'number' && Number.isFinite(daysRemaining);
  const safeDaysRemaining = hasValidDaysRemaining ? Math.max(daysRemaining, 0) : 0;
  const forecastDays = hasValidDaysRemaining ? safeDaysRemaining : 0;

  const calendarDaysRemainingRaw = getDaysLeftInMonthIncludingToday(calendarToday);
  const hasCalendarDaysRemaining =
    typeof calendarDaysRemainingRaw === 'number' && Number.isFinite(calendarDaysRemainingRaw);
  const calendarDaysRemaining = hasCalendarDaysRemaining
    ? Math.max(calendarDaysRemainingRaw, 0)
    : null;

  const fallbackActualRaw = toFiniteNumber(
    row.total_spend_sum ?? merged.total_spend_sum
  );
  const fallbackActual =
    typeof fallbackActualRaw === 'number' && Number.isFinite(fallbackActualRaw)
      ? fallbackActualRaw
      : null;

  const pacingMonthToDateCandidate = toFiniteNumber(
    metrics.pacing_month_to_date_spend ??
      metrics.month_to_date_spend ??
      baseMeta.pacing_month_to_date_spend ??
      baseMeta.month_to_date_spend
  );
  let resolvedPacing = pacingMonthToDateCandidate;
  if (
    resolvedPacing === null &&
    typeof metaPacingMonthToDateWithFees === 'number' &&
    Number.isFinite(metaPacingMonthToDateWithFees)
  ) {
    const divisor = feeMultiplier || 1;
    if (divisor !== 0) {
      const derived = metaPacingMonthToDateWithFees / divisor;
      if (Number.isFinite(derived)) {
        resolvedPacing = derived;
      }
    }
  }

  let todaySpendCandidate = toFiniteNumber(
    metrics.today_spend ?? baseMeta.today_spend
  );

  if (
    todaySpendCandidate === null &&
    typeof metaTodaySpendWithFees === 'number' &&
    Number.isFinite(metaTodaySpendWithFees)
  ) {
    const divisor = feeMultiplier || 1;
    if (divisor !== 0) {
      const derivedToday = metaTodaySpendWithFees / divisor;
      if (Number.isFinite(derivedToday)) {
        todaySpendCandidate = derivedToday;
      }
    }
  }

  if (shouldFallbackToActuals && fallbackActual !== null) {
    if (todaySpendCandidate === null && typeof resolvedPacing === 'number') {
      const derivedToday = fallbackActual - resolvedPacing;
      if (Number.isFinite(derivedToday)) {
        todaySpendCandidate = derivedToday;
      }
    }

    if (todaySpendCandidate === null && resolvedPacing === null) {
      todaySpendCandidate = fallbackActual;
    }

    if (resolvedPacing === null && typeof todaySpendCandidate === 'number') {
      const derivedPacing = fallbackActual - todaySpendCandidate;
      if (Number.isFinite(derivedPacing)) {
        resolvedPacing = derivedPacing;
      }
    }
  }

  const hasResolvedPacing =
    typeof resolvedPacing === 'number' && Number.isFinite(resolvedPacing);
  const hasTodaySpend =
    typeof todaySpendCandidate === 'number' && Number.isFinite(todaySpendCandidate);

  const pacingMonthToDate = clampToZero(hasResolvedPacing ? resolvedPacing : 0);
  const todaySpend = clampToZero(hasTodaySpend ? todaySpendCandidate : 0);

  const hasTodaySpendWithFeesMeta =
    typeof metaTodaySpendWithFees === 'number' && Number.isFinite(metaTodaySpendWithFees);
  const hasPacingWithFeesMeta =
    typeof metaPacingMonthToDateWithFees === 'number' &&
    Number.isFinite(metaPacingMonthToDateWithFees);
  const hasMonthThroughYesterdayWithFeesMeta =
    typeof metaMonthThroughYesterdayWithFees === 'number' &&
    Number.isFinite(metaMonthThroughYesterdayWithFees);
  const todaySpendWithFees = hasTodaySpendWithFeesMeta
    ? clampToZero(metaTodaySpendWithFees)
    : todaySpend * feeMultiplier;

  let actualMonthToDate = pacingMonthToDate + todaySpend;
  if (shouldFallbackToActuals && fallbackActual !== null) {
    actualMonthToDate = Math.max(actualMonthToDate, fallbackActual, 0);
  }

  if (
    typeof metaActualMonthToDateWithFees === 'number' &&
    Number.isFinite(metaActualMonthToDateWithFees)
  ) {
    const divisor = feeMultiplier || 1;
    if (divisor !== 0) {
      const derivedActual = metaActualMonthToDateWithFees / divisor;
      if (Number.isFinite(derivedActual)) {
        actualMonthToDate = Math.max(actualMonthToDate, derivedActual, 0);
      }
    }
  }

  const spentWithFees =
    typeof metaActualMonthToDateWithFees === 'number' &&
    Number.isFinite(metaActualMonthToDateWithFees)
      ? clampToZero(metaActualMonthToDateWithFees)
      : actualMonthToDate * feeMultiplier;
  const pacingMonthToDateWithFees = hasPacingWithFeesMeta
    ? clampToZero(metaPacingMonthToDateWithFees)
    : pacingMonthToDate * feeMultiplier;

  let actualThroughYesterday = Math.max(
    actualMonthToDate - todaySpend,
    pacingMonthToDate,
    0
  );
  if (hasMonthThroughYesterdayWithFeesMeta) {
    const divisor = feeMultiplier || 1;
    if (divisor !== 0) {
      const derivedThrough = metaMonthThroughYesterdayWithFees / divisor;
      if (Number.isFinite(derivedThrough)) {
        actualThroughYesterday = Math.max(actualThroughYesterday, derivedThrough, 0);
      }
    }
  }
  const computedMonthThroughYesterdayWithFees = Math.max(
    spentWithFees - todaySpendWithFees,
    pacingMonthToDateWithFees,
    actualThroughYesterday * feeMultiplier,
    0
  );
  const monthThroughYesterdayWithFees = hasMonthThroughYesterdayWithFeesMeta
    ? clampToZero(metaMonthThroughYesterdayWithFees)
    : clampToZero(computedMonthThroughYesterdayWithFees);
  const actualThroughYesterdayWithFees = monthThroughYesterdayWithFees;

  merged.month_to_date_spend = roundToTwo(spentWithFees);
  merged.budget_amount = roundToTwo(budgetAmount);

  const budgetRemaining = clampToZero(budgetAmount - spentWithFees);

  const projectionDaysRaw =
    calendarDaysRemaining !== null ? calendarDaysRemaining : forecastDays;
  const hasProjectionDays =
    typeof projectionDaysRaw === 'number' && Number.isFinite(projectionDaysRaw);
  const projectionDays = hasProjectionDays ? Math.max(projectionDaysRaw, 0) : null;

  const projectedCandidate =
    projectionDays !== null
      ? monthThroughYesterdayWithFees + yesterdaySpendWithFees * projectionDays
      : monthThroughYesterdayWithFees;

  let projected = Number.isFinite(projectedCandidate)
    ? projectedCandidate
    : monthThroughYesterdayWithFees;
  projected = Math.max(projected, spentWithFees, monthThroughYesterdayWithFees);
  projected = clampToZero(projected);

  const remainingPerDay =
    (() => {
      const backendRemainingPerDay = toFiniteNumber(
        metrics.remaining_per_day ??
          baseMeta.remaining_per_day ??
          merged.remaining_per_day
      );
      if (typeof backendRemainingPerDay === 'number' && Number.isFinite(backendRemainingPerDay)) {
        return clampToZero(backendRemainingPerDay);
      }
      return forecastDays > 0 ? clampToZero(budgetRemaining / forecastDays) : 0;
    })();
  const pacingPercentage =
    budgetAmount > 0 ? clampToZero((projected / budgetAmount) * 100) : 0;

  const calendarBudgetRemainingPerDay =
    (() => {
      const backendRemainingPerDay = toFiniteNumber(
        metrics.remaining_per_day ??
          baseMeta.remaining_per_day ??
          merged.remaining_per_day
      );
      if (typeof backendRemainingPerDay === 'number' && Number.isFinite(backendRemainingPerDay)) {
        return backendRemainingPerDay;
      }
      if (
        typeof calendarDaysRemaining === 'number' &&
        Number.isFinite(calendarDaysRemaining) &&
        calendarDaysRemaining > 0
      ) {
        return budgetRemaining / (feeMultiplier || 1) / calendarDaysRemaining;
      }
      return null;
    })();

  merged.budget_remaining = roundToTwo(budgetRemaining);
  merged.projected_spend = roundToTwo(projected);
  merged.remaining_per_day = roundToTwo(remainingPerDay);
  merged.pacing_percentage = roundToTwo(pacingPercentage);
  if (
    typeof calendarBudgetRemainingPerDay === 'number' &&
    Number.isFinite(calendarBudgetRemainingPerDay)
  ) {
    merged.calendar_budget_remaining_per_day = roundToTwo(
      calendarBudgetRemainingPerDay
    );
  } else {
    delete merged.calendar_budget_remaining_per_day;
  }
  if (hasValidYesterday) {
    merged.yesterday_spend = roundToTwo(yesterdaySpendWithFees);
    merged.yesterday_spend_with_fees = roundToTwo(yesterdaySpendWithFees);
    if (hasRawYesterday) {
      merged.yesterday_spend_without_fees = roundToTwo(yesterdaySpendRaw);
    } else {
      delete merged.yesterday_spend_without_fees;
    }
  } else {
    delete merged.yesterday_spend_with_fees;
    delete merged.yesterday_spend_without_fees;
  }

  const nextMeta = {
    ...baseMeta,
    spent_with_fees: spentWithFees,
    actual_month_to_date_with_fees: spentWithFees,
    actual_month_to_date_spend: actualMonthToDate,
    actual_through_yesterday_spend: actualThroughYesterday,
    actual_through_yesterday_with_fees: actualThroughYesterdayWithFees,
    month_through_yesterday_with_fees: monthThroughYesterdayWithFees,
  };

  if (hasValidDaysRemaining) {
    nextMeta.days_remaining = safeDaysRemaining;
    nextMeta.safe_days_remaining = safeDaysRemaining;
    nextMeta.forecast_days = forecastDays;
  } else {
    delete nextMeta.days_remaining;
    delete nextMeta.safe_days_remaining;
    delete nextMeta.forecast_days;
  }

  if (hasValidYesterday) {
    nextMeta.yesterday_spend = yesterdaySpendWithFees;
    nextMeta.yesterday_spend_with_fees = yesterdaySpendWithFees;
    if (hasRawYesterday) {
      nextMeta.yesterday_spend_without_fees = yesterdaySpendRaw;
    } else {
      delete nextMeta.yesterday_spend_without_fees;
    }
  } else {
    delete nextMeta.yesterday_spend;
    delete nextMeta.yesterday_spend_with_fees;
    delete nextMeta.yesterday_spend_without_fees;
  }

  if (
    hasResolvedPacing ||
    hasPacingWithFeesMeta ||
    (shouldFallbackToActuals && fallbackActual !== null)
  ) {
    nextMeta.pacing_month_to_date_spend = pacingMonthToDate;
    nextMeta.month_to_date_spend = pacingMonthToDate;
    nextMeta.pacing_month_to_date_with_fees = pacingMonthToDateWithFees;
  } else {
    delete nextMeta.pacing_month_to_date_spend;
    delete nextMeta.month_to_date_spend;
    delete nextMeta.pacing_month_to_date_with_fees;
  }

  if (
    hasTodaySpend ||
    hasTodaySpendWithFeesMeta ||
    (shouldFallbackToActuals && fallbackActual !== null)
  ) {
    nextMeta.today_spend = todaySpend;
    nextMeta.today_spend_with_fees = todaySpendWithFees;
  } else {
    delete nextMeta.today_spend;
    delete nextMeta.today_spend_with_fees;
  }

  if (
    typeof calendarDaysRemaining === 'number' &&
    Number.isFinite(calendarDaysRemaining)
  ) {
    nextMeta.calendar_days_remaining = calendarDaysRemaining;
  } else {
    delete nextMeta.calendar_days_remaining;
  }

  if (
    typeof calendarBudgetRemainingPerDay === 'number' &&
    Number.isFinite(calendarBudgetRemainingPerDay)
  ) {
    nextMeta.calendar_budget_remaining_per_day = calendarBudgetRemainingPerDay;
  } else {
    delete nextMeta.calendar_budget_remaining_per_day;
  }

  Object.keys(nextMeta).forEach((key) => {
    const value = nextMeta[key];
    if (value === undefined || value === null) {
      delete nextMeta[key];
    } else if (typeof value === 'number' && !Number.isFinite(value)) {
      delete nextMeta[key];
    }
  });

  if (Object.keys(nextMeta).length > 0) {
    merged.__pacing_metadata = nextMeta;
  } else {
    delete merged.__pacing_metadata;
  }

  const inheritedMeta = merged.__pacing_metadata;
  if (inheritedMeta && Array.isArray(merged.vertical_budget_breakdown)) {
    merged.vertical_budget_breakdown = merged.vertical_budget_breakdown.map((item) => {
      if (!item || typeof item !== 'object') {
        return item;
      }

      const existingMeta = extractPacingMeta(item);
      const combinedMeta =
        existingMeta && typeof existingMeta === 'object' ? { ...existingMeta } : {};

      let shouldAttachMeta = false;
      const assignMetaValue = (key) => {
        const value = inheritedMeta[key];
        if (value === undefined) {
          return;
        }
        if (combinedMeta[key] !== value) {
          combinedMeta[key] = value;
        }
        shouldAttachMeta = true;
      };

      assignMetaValue('forecast_days');
      assignMetaValue('calendar_days_remaining');
      assignMetaValue('calendar_budget_remaining_per_day');
      assignMetaValue('month_through_yesterday_with_fees');
      assignMetaValue('actual_through_yesterday_with_fees');
      assignMetaValue('actual_through_yesterday_spend');
      assignMetaValue('pacing_month_to_date_with_fees');
      assignMetaValue('actual_month_to_date_with_fees');
      assignMetaValue('today_spend_with_fees');
      assignMetaValue('spent_with_fees');

      if (!shouldAttachMeta) {
        return item;
      }

      return {
        ...item,
        __pacing_metadata: combinedMeta,
      };
    });
  }

  return merged;
};

const withMarketingMetrics = (row) => {
  if (!row || typeof row !== 'object') {
    return row;
  }

  const clientFee = toFiniteNumber(row.client_fee) ?? 0;
  const spend = numberOrZero(row.total_spend_sum);
  const netAmount = toFiniteNumber(row.net_amount_sum);
  const existingGoalRatio =
    typeof row.costOfMarketingGoal === 'number' ? row.costOfMarketingGoal : null;
  const rawGoalPercent = toFiniteNumber(row.cost_of_marketing_goal);
  const costOfMarketingGoal =
    existingGoalRatio ?? (typeof rawGoalPercent === 'number' ? rawGoalPercent / 100 : null);

  const spendPlusFees = spend * (1 + clientFee);
  const hasNetRevenue = typeof netAmount === 'number' && netAmount !== 0;
  const costOfMarketing = hasNetRevenue ? spendPlusFees / netAmount : null;

  return {
    ...row,
    client_fee: clientFee,
    spendPlusFees,
    costOfMarketing,
    costOfMarketingGoal,
  };
};

const DailyPulseContext = React.createContext(null);
export const useDailyPulse = () => useContext(DailyPulseContext);

const DAILY_PULSE_CACHE = new Map();
const DAILY_PULSE_CACHE_MAX_SIZE = 20;
// Single full fetch: the backend builds the complete row list on every request
// and only slices it for pagination, so requesting everything costs the same
// while letting the page group/sort/search across all clients at once.
const FULL_FETCH_PAGE_SIZE = 500;

export const DailyPulseProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [data, setData] = useState([]);
  const [tree, setTree] = useState([]);
  const [clientSummary, setClientSummary] = useState(null);
  const [enableRolling, setEnableRolling] = useState(true);
  const { isAuthenticated, state: authState } = useAuth();

  const [monthStart, monthEnd] = useMemo(() => getCurrentMonthDates(), []);
  const [startDate, setStartDate] = useState(monthStart);
  const [endDate, setEndDate] = useState(monthEnd);
  const [clientStatusFilter, setClientStatusFilter] = useState(DEFAULT_CLIENT_STATUS_FILTER);
  const [clientTierFilter, setClientTierFilter] = useState([]);
  const [clientTotalCount, setClientTotalCount] = useState(0);
  const [refreshToken, setRefreshToken] = useState(0);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const handledRefreshTokenRef = useRef(0);
  const cacheScope = useMemo(
    () => authState?.user?.id ?? authState?.user?.email ?? null,
    [authState?.user?.id, authState?.user?.email]
  );

  const monthAnchorDate = useMemo(() => {
    const source = monthStart instanceof Date ? monthStart : new Date(monthStart);
    if (!(source instanceof Date) || Number.isNaN(source.getTime())) {
      return null;
    }
    return source;
  }, [monthStart]);

  // Anchor pacing to the selected end date, clamped to "now" so backend and frontend
  // agree on what "today" and "yesterday" mean.
  const pacingAnchorDate = useMemo(() => {
    // Anchor "now" to New York wall-clock so pacing windows match the
    // America/New_York dates the spend data is keyed by, regardless of
    // where the viewer is.
    const now = getNewYorkNow();
    const hasValidNow = now instanceof Date && !Number.isNaN(now.getTime());
    const end = endDate instanceof Date ? endDate : endDate ? new Date(endDate) : null;
    const hasValidEnd = end instanceof Date && !Number.isNaN(end?.getTime?.());

    if (hasValidEnd && hasValidNow) {
      return end.getTime() <= now.getTime() ? end : now;
    }
    if (hasValidEnd) {
      return end;
    }
    return hasValidNow ? now : null;
  }, [endDate]);

  const calendarToday = useMemo(() => {
    const resolvedMonthEnd =
      monthEnd instanceof Date ? monthEnd : monthEnd ? new Date(monthEnd) : null;
    const hasValidMonthEnd =
      resolvedMonthEnd instanceof Date && !Number.isNaN(resolvedMonthEnd?.getTime?.());

    if (pacingAnchorDate && monthAnchorDate) {
      const sameMonth =
        pacingAnchorDate.getUTCFullYear() === monthAnchorDate.getUTCFullYear() &&
        pacingAnchorDate.getUTCMonth() === monthAnchorDate.getUTCMonth();

      if (sameMonth) {
        return pacingAnchorDate;
      }
    }

    if (hasValidMonthEnd) {
      return resolvedMonthEnd;
    }

    return pacingAnchorDate;
  }, [monthEnd, monthAnchorDate, pacingAnchorDate]);

  const isCurrentMonthRange = useMemo(() => {
    if (!monthAnchorDate) {
      return false;
    }
    const start = startDate instanceof Date ? startDate : new Date(startDate);
    const end = endDate instanceof Date ? endDate : new Date(endDate);
    if (!(start instanceof Date) || Number.isNaN(start.getTime())) {
      return false;
    }
    if (!(end instanceof Date) || Number.isNaN(end.getTime())) {
      return false;
    }
    return (
      start.getUTCFullYear() === monthAnchorDate.getUTCFullYear() &&
      start.getUTCMonth() === monthAnchorDate.getUTCMonth() &&
      end.getUTCFullYear() === monthAnchorDate.getUTCFullYear() &&
      end.getUTCMonth() === monthAnchorDate.getUTCMonth()
    );
  }, [startDate, endDate, monthAnchorDate]);

  const baseFilters = useMemo(() => {
    const filters = {
      date_after: formatDateToYMD(startDate),
      date_before: formatDateToYMD(endDate),
    };
    if (Array.isArray(clientStatusFilter) && clientStatusFilter.length > 0) {
      filters.client_status = clientStatusFilter.join(',');
    }
    if (Array.isArray(clientTierFilter) && clientTierFilter.length > 0) {
      filters.client_tier = clientTierFilter.join(',');
    }
    return filters;
  }, [startDate, endDate, clientStatusFilter, clientTierFilter]);

  const rollingRanges = useMemo(
    () => ({
      last30: buildTrailingRange(30),
      last60: buildTrailingRange(60),
    }),
    []
  );

  const rollingBaseFilters = useMemo(() => {
    const { date_after, date_before, ...rest } = baseFilters;
    return rest;
  }, [baseFilters]);

  const buildPacingLookup = useCallback((budgets, fallbackBudgets = []) => {
    const clientMap = new Map();
    const channelMap = new Map();
    const fallbackLookup = buildYesterdayFallbackLookup(fallbackBudgets);

    const ensureBucket = (map, key) => {
      if (!map.has(key)) {
        map.set(key, {
          budget_amount: 0,
          projected_spend: 0,
          month_to_date_spend: 0,
          today_spend: 0,
          has_today_spend: false,
          today_spend_with_fees: 0,
          has_today_spend_with_fees: false,
          budget_remaining: 0,
          remaining_per_day: 0,
          yesterday_spend: 0,
          yesterday_spend_with_fees: 0,
          has_yesterday_with_fees: false,
          pacing_month_to_date_with_fees: 0,
          has_pacing_month_to_date_with_fees: false,
          month_through_yesterday_with_fees: 0,
          has_month_through_yesterday_with_fees: false,
          actual_month_to_date_with_fees: 0,
          has_actual_month_to_date_with_fees: false,
          verticals: new Map(),
        });
      }
      return map.get(key);
    };

    const addMetrics = (bucket, metrics) => {
      bucket.budget_amount += metrics.amount;
      bucket.projected_spend += metrics.projected_spend;
      bucket.month_to_date_spend += metrics.month_to_date_spend;
      bucket.budget_remaining += metrics.budget_remaining;
      bucket.remaining_per_day += metrics.remaining_per_day;
      bucket.yesterday_spend += metrics.yesterday_spend;
      if (
        typeof metrics.yesterday_spend_with_fees === 'number' &&
        Number.isFinite(metrics.yesterday_spend_with_fees)
      ) {
        bucket.yesterday_spend_with_fees += metrics.yesterday_spend_with_fees;
        bucket.has_yesterday_with_fees = true;
      }
      if (typeof metrics.today_spend === 'number' && Number.isFinite(metrics.today_spend)) {
        bucket.today_spend += metrics.today_spend;
        bucket.has_today_spend = true;
      }
      if (
        typeof metrics.today_spend_with_fees === 'number' &&
        Number.isFinite(metrics.today_spend_with_fees)
      ) {
        bucket.today_spend_with_fees += metrics.today_spend_with_fees;
        bucket.has_today_spend_with_fees = true;
      }
      if (
        typeof metrics.pacing_month_to_date_with_fees === 'number' &&
        Number.isFinite(metrics.pacing_month_to_date_with_fees)
      ) {
        bucket.pacing_month_to_date_with_fees += metrics.pacing_month_to_date_with_fees;
        bucket.has_pacing_month_to_date_with_fees = true;
      }
      if (
        typeof metrics.month_through_yesterday_with_fees === 'number' &&
        Number.isFinite(metrics.month_through_yesterday_with_fees)
      ) {
        bucket.month_through_yesterday_with_fees +=
          metrics.month_through_yesterday_with_fees;
        bucket.has_month_through_yesterday_with_fees = true;
      }
      if (
        typeof metrics.actual_month_to_date_with_fees === 'number' &&
        Number.isFinite(metrics.actual_month_to_date_with_fees)
      ) {
        bucket.actual_month_to_date_with_fees += metrics.actual_month_to_date_with_fees;
        bucket.has_actual_month_to_date_with_fees = true;
      }
    };

    budgets.forEach((budget) => {
      const metrics = {
        amount: numberOrZero(budget.amount),
        projected_spend: numberOrZero(budget.projected_spend),
        month_to_date_spend: numberOrZero(budget.month_to_date_spend),
        budget_remaining: numberOrZero(budget.budget_remaining),
        remaining_per_day: numberOrZero(budget.remaining_per_day),
        yesterday_spend: numberOrZero(
          budget.yesterday_spend ??
            budget?.__pacing_metadata?.yesterday_spend_without_fees ??
            budget?.pacing_metadata?.yesterday_spend_without_fees
        ),
        yesterday_spend_with_fees: toFiniteNumber(
          budget.yesterday_spend_with_fees ??
            budget?.__pacing_metadata?.yesterday_spend_with_fees ??
            budget?.pacing_metadata?.yesterday_spend_with_fees ??
            budget?.__pacing_metadata?.yesterday_spend ??
            budget?.pacing_metadata?.yesterday_spend
        ),
        today_spend: toFiniteNumber(
          budget.today_spend ??
            budget?.__pacing_metadata?.today_spend ??
            budget?.pacing_metadata?.today_spend
        ),
        today_spend_with_fees: toFiniteNumber(
          budget.today_spend_with_fees ??
            budget?.__pacing_metadata?.today_spend_with_fees ??
            budget?.pacing_metadata?.today_spend_with_fees
        ),
        pacing_month_to_date_with_fees: toFiniteNumber(
          budget.pacing_month_to_date_with_fees ??
            budget?.__pacing_metadata?.pacing_month_to_date_with_fees ??
            budget?.pacing_metadata?.pacing_month_to_date_with_fees ??
            budget?.__pacing_metadata?.month_through_yesterday_with_fees ??
            budget?.pacing_metadata?.month_through_yesterday_with_fees
        ),
        month_through_yesterday_with_fees: toFiniteNumber(
          budget.month_through_yesterday_with_fees ??
            budget?.__pacing_metadata?.month_through_yesterday_with_fees ??
            budget?.__pacing_metadata?.actual_through_yesterday_with_fees ??
            budget?.pacing_metadata?.month_through_yesterday_with_fees ??
            budget?.pacing_metadata?.actual_through_yesterday_with_fees
        ),
        actual_month_to_date_with_fees: toFiniteNumber(
          budget.actual_month_to_date_with_fees ??
            budget?.__pacing_metadata?.actual_month_to_date_with_fees ??
            budget?.__pacing_metadata?.spent_with_fees ??
            budget?.pacing_metadata?.actual_month_to_date_with_fees ??
            budget?.pacing_metadata?.spent_with_fees
        ),
      };

      const clientKey = `client-${budget.client}`;
      const clientBucket = ensureBucket(clientMap, clientKey);
      addMetrics(clientBucket, metrics);
      // Track worst-case workflow status: draft < pending_approval < approved < active
      const wfStatus = (budget.workflow_status || '').toLowerCase();
      if (!clientBucket.budget_workflow_status || wfStatus === 'draft') {
        clientBucket.budget_workflow_status = wfStatus;
      }

      const verticalName = (budget.vertical_data?.vertical || '').trim();
      if (verticalName) {
        const verticalBucket = ensureBucket(clientBucket.verticals, verticalName);
        addMetrics(verticalBucket, metrics);
      }

      if (budget.channel) {
        const channelKey = `client-${budget.client}-channel-${budget.channel}`;
        const channelBucket = ensureBucket(channelMap, channelKey);
        addMetrics(channelBucket, metrics);

        if (verticalName) {
          const channelVerticalBucket = ensureBucket(channelBucket.verticals, verticalName);
          addMetrics(channelVerticalBucket, metrics);
        }
      }
    });

    const finalizeBucket = (bucket, key, fallbackMap = null) => {
      const amount = bucket.budget_amount;
      const projected = bucket.projected_spend;
      const budgetRemaining = bucket.budget_remaining;
      const remainingPerDay = bucket.remaining_per_day;
      const yesterdaySpend = bucket.has_yesterday_with_fees
        ? bucket.yesterday_spend_with_fees
        : bucket.yesterday_spend;
      const todaySpend = bucket.has_today_spend ? bucket.today_spend : null;
      const todaySpendWithFees = bucket.has_today_spend_with_fees
        ? bucket.today_spend_with_fees
        : null;
      const pacingMonthToDateWithFees = bucket.has_pacing_month_to_date_with_fees
        ? bucket.pacing_month_to_date_with_fees
        : null;
      const fallbackEntry = fallbackMap?.get(key);
      const fallbackYesterdaySpendWithFees =
        fallbackEntry && typeof fallbackEntry.yesterday_spend_with_fees === 'number'
          ? fallbackEntry.yesterday_spend_with_fees
          : null;
      const fallbackMonthThroughYesterdayWithFees =
        fallbackEntry && typeof fallbackEntry.month_through_yesterday_with_fees === 'number'
          ? fallbackEntry.month_through_yesterday_with_fees
          : null;
      const fallbackActualThroughYesterdayWithFees =
        fallbackEntry && typeof fallbackEntry.actual_through_yesterday_with_fees === 'number'
          ? fallbackEntry.actual_through_yesterday_with_fees
          : null;

      const resolvedYesterdaySpendWithFees = bucket.has_yesterday_with_fees
        ? bucket.yesterday_spend_with_fees
        : fallbackYesterdaySpendWithFees;
      const resolvedYesterdaySpend =
        bucket.has_yesterday_with_fees || fallbackYesterdaySpendWithFees !== null
          ? resolvedYesterdaySpendWithFees
          : bucket.yesterday_spend;
      const monthThroughYesterdayWithFees =
        bucket.has_month_through_yesterday_with_fees
          ? bucket.month_through_yesterday_with_fees
          : fallbackMonthThroughYesterdayWithFees;
      const actualMonthToDateWithFees =
        bucket.has_actual_month_to_date_with_fees
          ? bucket.actual_month_to_date_with_fees
          : fallbackActualThroughYesterdayWithFees ?? fallbackMonthThroughYesterdayWithFees;
      const daysRemaining = deriveDaysRemaining(budgetRemaining, remainingPerDay);
      const pacingMeta = {};
      if (typeof daysRemaining === 'number' && Number.isFinite(daysRemaining)) {
        pacingMeta.days_remaining = daysRemaining;
      }
      const hasResolvedYesterday =
        typeof resolvedYesterdaySpend === 'number' && Number.isFinite(resolvedYesterdaySpend);
      const hasResolvedYesterdayWithFees =
        typeof resolvedYesterdaySpendWithFees === 'number' &&
        Number.isFinite(resolvedYesterdaySpendWithFees);
      if (hasResolvedYesterday) {
        pacingMeta.yesterday_spend = resolvedYesterdaySpend;
        if (hasResolvedYesterdayWithFees) {
          pacingMeta.yesterday_spend_with_fees = resolvedYesterdaySpendWithFees;
        }
      }
      if (typeof todaySpend === 'number' && Number.isFinite(todaySpend)) {
        pacingMeta.today_spend = todaySpend;
      }
      if (
        typeof todaySpendWithFees === 'number' &&
        Number.isFinite(todaySpendWithFees)
      ) {
        pacingMeta.today_spend_with_fees = todaySpendWithFees;
      }
      if (typeof bucket.month_to_date_spend === 'number' && Number.isFinite(bucket.month_to_date_spend)) {
        pacingMeta.pacing_month_to_date_spend = bucket.month_to_date_spend;
        pacingMeta.month_to_date_spend = bucket.month_to_date_spend;
        if (
          typeof pacingMonthToDateWithFees === 'number' &&
          Number.isFinite(pacingMonthToDateWithFees)
        ) {
          pacingMeta.pacing_month_to_date_with_fees = pacingMonthToDateWithFees;
        }
        const actualThroughYesterdaySpend = Math.max(
          typeof todaySpend === 'number' && Number.isFinite(todaySpend)
            ? bucket.month_to_date_spend - todaySpend
            : bucket.month_to_date_spend,
          bucket.month_to_date_spend,
          0
        );
        if (Number.isFinite(actualThroughYesterdaySpend)) {
          pacingMeta.actual_through_yesterday_spend = actualThroughYesterdaySpend;
        }
      }
      const hasResolvedMonthThroughYesterday =
        typeof monthThroughYesterdayWithFees === 'number' &&
        Number.isFinite(monthThroughYesterdayWithFees);
      if (hasResolvedMonthThroughYesterday) {
        pacingMeta.month_through_yesterday_with_fees = monthThroughYesterdayWithFees;
        if (
          typeof actualMonthToDateWithFees === 'number' &&
          Number.isFinite(actualMonthToDateWithFees)
        ) {
          pacingMeta.actual_through_yesterday_with_fees = actualMonthToDateWithFees;
        } else {
          pacingMeta.actual_through_yesterday_with_fees = monthThroughYesterdayWithFees;
        }
      } else if (
        typeof pacingMonthToDateWithFees === 'number' &&
        Number.isFinite(pacingMonthToDateWithFees)
      ) {
        pacingMeta.month_through_yesterday_with_fees = pacingMonthToDateWithFees;
      }
      if (
        typeof actualMonthToDateWithFees === 'number' &&
        Number.isFinite(actualMonthToDateWithFees)
      ) {
        pacingMeta.actual_month_to_date_with_fees = actualMonthToDateWithFees;
        pacingMeta.spent_with_fees = actualMonthToDateWithFees;
      }
      const projectedRemainingBudget =
        typeof amount === 'number' && Number.isFinite(amount) &&
        typeof projected === 'number' && Number.isFinite(projected)
          ? amount - projected
          : null;

      const result = {
        budget_amount: roundToTwo(amount),
        projected_spend: roundToTwo(projected),
        month_to_date_spend: roundToTwo(bucket.month_to_date_spend),
        budget_remaining: roundToTwo(budgetRemaining),
        remaining_per_day: roundToTwo(remainingPerDay),
        pacing_percentage: amount > 0 ? roundToTwo((projected / amount) * 100) : 0,
        yesterday_spend: roundToTwo(resolvedYesterdaySpend),
      };
      if (
        typeof projectedRemainingBudget === 'number' &&
        Number.isFinite(projectedRemainingBudget)
      ) {
        result.calendar_budget_remaining = roundToTwo(projectedRemainingBudget);
      }
      if (todaySpend !== null) {
        result.today_spend = roundToTwo(todaySpend);
      }
      if (
        typeof todaySpendWithFees === 'number' &&
        Number.isFinite(todaySpendWithFees)
      ) {
        result.today_spend_with_fees = roundToTwo(todaySpendWithFees);
      }
      if (hasResolvedYesterdayWithFees) {
        result.yesterday_spend_with_fees = roundToTwo(resolvedYesterdaySpendWithFees);
      }
      if (Object.keys(pacingMeta).length > 0) {
        result.__pacing_metadata = pacingMeta;
      }

      if (bucket.budget_workflow_status) {
        result.budget_workflow_status = bucket.budget_workflow_status;
      }

      if (bucket.verticals.size) {
        result.vertical_budget_breakdown = Array.from(bucket.verticals.entries()).map(
          ([name, vertBucket]) => ({
            vertical: name,
            ...finalizeBucket(vertBucket),
          })
        );
      }

      return result;
    };

    const finalizeMap = (map, fallbackMap) => {
      const output = {};
      map.forEach((bucket, key) => {
        output[key] = finalizeBucket(bucket, key, fallbackMap);
      });
      return output;
    };

    return {
      clients: finalizeMap(clientMap, fallbackLookup.clients),
      channels: finalizeMap(channelMap, fallbackLookup.channels),
    };
  }, []);

  const mergeMetrics = useCallback(
    (row, metrics) => {
      if (!metrics) {
        return row;
      }

      const merged = { ...row, ...metrics };

      if (metrics.vertical_budget_breakdown) {
        merged.vertical_budget_breakdown = metrics.vertical_budget_breakdown.map((item) => ({
          ...item,
        }));
      }

      return recalculateBudgetMetrics(row, metrics, merged, {
        shouldFallbackToActuals: isCurrentMonthRange,
        calendarToday,
      });
    },
    [isCurrentMonthRange, calendarToday]
  );

  const applyClientMetrics = useCallback(
    (clients, lookup, overallByClient = new Map()) =>
      (Array.isArray(clients) ? clients : []).map((client) => {
        const key = `client-${client.client_id}`;
        const metrics = lookup.clients[key];
        const merged = mergeMetrics({ ...client }, metrics);
        const overall = overallByClient.get(client.client_id);
        const mergedWithOverall = overall
          ? {
              ...merged,
              ...overall,
              // Keep remaining/day aligned with canonical pacing-overall values
              // when we have them, otherwise fall back to merged calculations.
              remaining_per_day:
                overall.remaining_per_day ?? merged.remaining_per_day,
              calendar_budget_remaining_per_day:
                overall.remaining_per_day ??
                merged.calendar_budget_remaining_per_day,
              __pacing_metadata: {
                ...extractPacingMeta(merged),
                ...extractPacingMeta(overall),
              },
            }
          : merged;
        return withMarketingMetrics(mergedWithOverall);
      }),
    [mergeMetrics]
  );

  useEffect(() => {
    let cancelled = false;

    if (!isAuthenticated) {
      return;
    }

    const [currentMonthStart] = getCurrentMonthDates();
    const monthSource =
      currentMonthStart instanceof Date
        ? currentMonthStart
        : new Date(currentMonthStart);
    const pacingPayload = {
      // Local getters, not getUTC*: monthSource is a local-midnight Date, so in
      // UTC+ timezones the UTC month is the previous month (June pacing shown in July).
      month: monthSource.getMonth() + 1,
      year: monthSource.getFullYear(),
      ...(pacingAnchorDate ? { target_date: formatDateToYMD(pacingAnchorDate) } : {}),
      ...(Array.isArray(clientStatusFilter) && clientStatusFilter.length > 0
        ? { client_status: clientStatusFilter.join(',') }
        : {}),
      ...(Array.isArray(clientTierFilter) && clientTierFilter.length > 0
        ? { client_tier: clientTierFilter.join(',') }
        : {}),
    };

    const isFirstOfMonth =
      calendarToday instanceof Date && calendarToday.getDate() === 1;
    const previousMonthSource = new Date(monthSource);
    previousMonthSource.setMonth(previousMonthSource.getMonth() - 1);
    const previousMonthPayload = {
      month: previousMonthSource.getMonth() + 1,
      year: previousMonthSource.getFullYear(),
      ...(Array.isArray(clientStatusFilter) && clientStatusFilter.length > 0
        ? { client_status: clientStatusFilter.join(',') }
        : {}),
      ...(Array.isArray(clientTierFilter) && clientTierFilter.length > 0
        ? { client_tier: clientTierFilter.join(',') }
        : {}),
    };
    const shouldBypassCache = refreshToken !== handledRefreshTokenRef.current;
    if (shouldBypassCache) {
      handledRefreshTokenRef.current = refreshToken;
    }
    const cacheKey = JSON.stringify({
      baseFilters,
      rollingBaseFilters,
      rollingRanges,
      enableRolling,
      cacheScope,
      pacingPayload,
      previousMonthPayload: isFirstOfMonth ? previousMonthPayload : null,
    });
    const cached = DAILY_PULSE_CACHE.get(cacheKey);

    if (cached && !shouldBypassCache) {
      setTree(cached.tree);
      setClientSummary(cached.clientSummary ?? null);
      setClientTotalCount(cached.totalCount ?? (cached.tree || []).length);
      setLastRefreshedAt(cached.refreshedAt);
      setIsError(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const pacingPromise = fetchBudgetPacing(pacingPayload).catch((error) => {
      console.error('Error fetching pacing data', error);
      return { data: [] };
    });
    const pacingOverallPromise = fetchBudgetPacingOverall(pacingPayload).catch((error) => {
      console.error('Error fetching overall pacing data', error);
      return { data: [] };
    });
    const previousMonthPacingPromise = isFirstOfMonth
      ? fetchBudgetPacing(previousMonthPayload).catch((error) => {
          console.error('Error fetching previous month pacing data', error);
          return { data: [] };
        })
      : Promise.resolve({ data: [] });
    const clientPromise = fetchDailyPulseByClient({
      ...baseFilters,
      page: 1,
      page_size: FULL_FETCH_PAGE_SIZE,
    });

    // Rolling 30/60d data only feeds the cost-of-marketing tooltips, so the
    // requests start now but their results merge in after the first paint
    // instead of blocking it. They skip ingestion health on the backend.
    const rollingRequestBase = {
      ...rollingBaseFilters,
      include_ingestion_health: 0,
      page: 1,
      page_size: FULL_FETCH_PAGE_SIZE,
    };
    const clientLast30Promise = enableRolling && rollingRanges.last30
      ? fetchDailyPulseByClient({
          ...rollingRequestBase,
          ...rollingRanges.last30,
        }).catch((error) => {
          console.error('Error fetching last 30 day Daily Pulse data', error);
          return { data: [] };
        })
      : Promise.resolve({ data: [] });
    const clientLast60Promise = enableRolling && rollingRanges.last60
      ? fetchDailyPulseByClient({
          ...rollingRequestBase,
          ...rollingRanges.last60,
        }).catch((error) => {
          console.error('Error fetching last 60 day Daily Pulse data', error);
          return { data: [] };
        })
      : Promise.resolve({ data: [] });

    const mainPromise = Promise.all([
      clientPromise,
      pacingPromise,
      pacingOverallPromise,
      previousMonthPacingPromise,
    ])
      .then(([
        pulseResp,
        pacingResp,
        pacingOverallResp,
        previousPacingResp,
      ]) => {
        const budgets = Array.isArray(pacingResp?.data) ? pacingResp.data : [];
        const fallbackBudgets = Array.isArray(previousPacingResp?.data)
          ? previousPacingResp.data
          : [];
        const lookup = buildPacingLookup(budgets, fallbackBudgets);

        const overallRows = Array.isArray(pacingOverallResp?.data)
          ? pacingOverallResp.data
          : [];
        const overallSummary = summarizeOverallBudgetRows(overallRows);
        const overallByClient = new Map();
        overallRows.forEach((row = {}) => {
          const clientId = row.client_id;
          if (!clientId) {
            return;
          }
          overallByClient.set(clientId, row);
        });

        const clients = extractRowsFromResponse(pulseResp);
        const totalCount = extractCountFromResponse(pulseResp, clients.length);
        const baseSummary = extractSummaryFromResponse(pulseResp);
        const hydratedTree = applyClientMetrics(
          clients,
          lookup,
          overallByClient
        );
        const hydratedSummary = mergeClientSummary(
          baseSummary,
          overallSummary,
          {}
        );
        const refreshedAt = Date.now();

        if (DAILY_PULSE_CACHE.size >= DAILY_PULSE_CACHE_MAX_SIZE) {
          const oldest = DAILY_PULSE_CACHE.keys().next().value;
          DAILY_PULSE_CACHE.delete(oldest);
        }
        DAILY_PULSE_CACHE.set(cacheKey, {
          tree: hydratedTree,
          clientSummary: hydratedSummary,
          totalCount,
          refreshedAt,
        });

        if (cancelled) return;
        setTree(hydratedTree);
        setClientSummary(hydratedSummary);
        setClientTotalCount(totalCount);
        setLastRefreshedAt(refreshedAt);
        setIsError(false);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error(error);
        setTree([]);
        setClientSummary(null);
        setClientTotalCount(0);
        setIsError(true);
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });

    if (enableRolling) {
      Promise.all([mainPromise, clientLast30Promise, clientLast60Promise])
        .then(([, last30Resp, last60Resp]) => {
          if (cancelled) return;
          const rollingLookup = buildRollingLookups(
            extractRowsFromResponse(last30Resp),
            extractRowsFromResponse(last60Resp),
            getClientLookupKey
          );
          const rollingSummaries = {
            last30: extractSummaryFromResponse(last30Resp),
            last60: extractSummaryFromResponse(last60Resp),
          };
          setTree((prev) => {
            const next = prev.map((row) =>
              attachRollingCosts(row, getClientLookupKey, rollingLookup, true)
            );
            const cachedEntry = DAILY_PULSE_CACHE.get(cacheKey);
            if (cachedEntry) {
              DAILY_PULSE_CACHE.set(cacheKey, { ...cachedEntry, tree: next });
            }
            return next;
          });
          setClientSummary((prev) => {
            if (!prev) {
              return prev;
            }
            const next = { ...prev };
            if (rollingSummaries.last30?.costOfMarketing !== undefined) {
              next.costOfMarketingLast30 = rollingSummaries.last30.costOfMarketing;
            }
            if (rollingSummaries.last60?.costOfMarketing !== undefined) {
              next.costOfMarketingLast60 = rollingSummaries.last60.costOfMarketing;
            }
            const cachedEntry = DAILY_PULSE_CACHE.get(cacheKey);
            if (cachedEntry) {
              DAILY_PULSE_CACHE.set(cacheKey, { ...cachedEntry, clientSummary: next });
            }
            return next;
          });
        })
        .catch((error) => {
          console.error('Error merging rolling Daily Pulse data', error);
        });
    }

    return () => { cancelled = true; };
  }, [
    isAuthenticated,
    cacheScope,
    baseFilters,
    rollingBaseFilters,
    rollingRanges,
    enableRolling,
    buildPacingLookup,
    calendarToday,
    applyClientMetrics,
    pacingAnchorDate,
    refreshToken,
  ]);

  useEffect(() => {
    setData(tree);
  }, [tree]);

  const setDateRange = useCallback((start, end) => {
    setStartDate(start);
    setEndDate(end);
  }, []);

  const refreshData = useCallback(() => {
    setRefreshToken((prev) => prev + 1);
  }, []);

  const value = useMemo(() => ({
    isLoading,
    isError,
    data,
    tree,
    clientSummary,
    startDate,
    endDate,
    setDateRange,
    refreshData,
    lastRefreshedAt,
    enableRolling,
    setEnableRolling,
    clientStatusFilter,
    setClientStatusFilter,
    clientTierFilter,
    setClientTierFilter,
    clientTotalCount,
  }), [
    isLoading,
    isError,
    data,
    tree,
    clientSummary,
    startDate,
    endDate,
    setDateRange,
    refreshData,
    lastRefreshedAt,
    enableRolling,
    clientStatusFilter,
    clientTierFilter,
    clientTotalCount,
  ]);

  return (
    <DailyPulseContext.Provider value={value}>
      {children}
    </DailyPulseContext.Provider>
  );
};
